/**
 * FormBuilderPage — create or edit a form schema.
 *
 * Features:
 *   - Name, description, aiMode, authMode, isPublic/slug
 *   - Three schema input tabs: Designer (Form.io drag-and-drop) | AI Builder | JSON Editor
 *   - Live @formio/react preview panel (hidden in Designer mode — the builder IS the preview)
 *   - Save (create or update)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { forms as formsApi, ai, FormSchema, IdentityFieldDef } from '../../../lib/api.js';
import {
  IDENTITY_FIELD_CATALOG, DEFAULT_IDENTITY_CONFIG,
  isIncluded, toggleField, setRequired, isDefaultConfig,
} from '../../../lib/identity-config.js';
import { SparkleIcon, CodeIcon, SaveIcon, CheckCircleIcon, SpinnerIcon } from './icons.js';

/** Drag-and-drop designer tab icon. */
function LayoutIcon({ size = 14 }: { size?: number }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

interface Props {
  editingSchema?: FormSchema | null | undefined;
  onSaved: (id: string) => void;
  onCancel: () => void;
}

type SchemaTab = 'designer' | 'ai' | 'json';

const AI_MODES = [
  { value: 'ai_only',   label: 'AI Chat Only' },
  { value: 'toggle',    label: 'AI + Manual Toggle' },
  { value: 'form_only', label: 'Manual Form Only' },
];

const AUTH_MODES = [
  { value: 'open',      label: 'Open (no authentication)' },
  { value: 'otp_email', label: 'OTP via Email' },
  { value: 'otp_phone', label: 'OTP via Phone' },
];

const EMPTY_FORM = '{\n  "display": "form",\n  "components": []\n}';

export default function FormBuilderPage({ editingSchema, onSaved, onCancel }: Props): React.ReactElement {
  const [name,        setName]        = useState(editingSchema?.name ?? '');
  const [description, setDesc]        = useState(editingSchema?.description ?? '');
  const [aiMode,      setAiMode]      = useState(editingSchema?.aiMode ?? 'toggle');
  const [authMode,    setAuthMode]    = useState(editingSchema?.authMode ?? 'otp_phone');
  const [isPublic,    setIsPublic]    = useState(editingSchema?.isPublic ?? false);
  const [publicSlug,  setSlug]        = useState(editingSchema?.publicSlug ?? '');
  // Patient-identity config. Absent on the schema (old API) → the DEFAULT trio,
  // so the builder round-trips. Empty array = staff opted out (no identity step).
  const [identityFields, setIdentityFields] = useState<IdentityFieldDef[]>(
    editingSchema?.identityFields ? [...editingSchema.identityFields] : [...DEFAULT_IDENTITY_CONFIG],
  );
  const [schemaTab,   setSchemaTab]   = useState<SchemaTab>('designer');
  const [schemaJson,  setSchemaJson]  = useState(editingSchema ? '{}' : EMPTY_FORM);
  const [jsonError,   setJsonError]   = useState<string | null>(null);
  const [saving,      setSaving]      = useState(false);
  const [saveError,   setSaveError]   = useState<string | null>(null);
  const [FormPreview, setPreview]     = useState<React.ComponentType<{ form: unknown; options: Record<string, unknown> }> | null>(null);
  const [FormBuilderC, setBuilderC]   = useState<React.ComponentType<{ form: unknown; options?: Record<string, unknown>; onChange?: (form: unknown) => void }> | null>(null);
  const [builderForm, setBuilderForm] = useState<{ display: string; components: unknown[] } | null>(null);
  const [schemaReady, setSchemaReady] = useState<boolean>(!editingSchema);
  const [aiPrompt,    setAiPrompt]    = useState('');
  const [aiBuilding,  setAiBuilding]  = useState(false);
  const [aiBuildError, setAiBuildError] = useState<string | null>(null);
  const [aiGenerated, setAiGenerated] = useState(false);

  // Load editingSchema's schemaJson
  useEffect(() => {
    if (editingSchema) {
      formsApi.schemas.get(editingSchema.id).then((res) => {
        if (res.success && res.data?.schemaJson) {
          setSchemaJson(JSON.stringify(res.data.schemaJson, null, 2));
        }
        if (res.success && res.data?.identityFields) {
          setIdentityFields([...res.data.identityFields]);
        }
      }).catch(() => {/* ignore */}).finally(() => setSchemaReady(true));
    }
  }, [editingSchema]);

  // Lazy-load @formio/react for the live preview AND the drag-and-drop designer.
  useEffect(() => {
    import('@formio/react').then((mod) => {
      const m = mod as unknown as {
        Form: React.ComponentType<{ form: unknown; options: Record<string, unknown> }>;
        FormBuilder: React.ComponentType<{ form: unknown; options?: Record<string, unknown>; onChange?: (form: unknown) => void }>;
      };
      setPreview(() => m.Form);
      setBuilderC(() => m.FormBuilder);
    }).catch(() => {/* no formio available */});
  }, []);

  // Seed the designer from the current schema when the Designer tab opens (and once
  // an edited form has loaded). Deliberately NOT keyed on schemaJson, so editing
  // inside the builder doesn't remount/reset it — changes flow OUT via onChange.
  useEffect(() => {
    if (schemaTab !== 'designer' || !schemaReady) return;
    try {
      const p = JSON.parse(schemaJson) as { display?: string; components?: unknown[] };
      setBuilderForm({ display: p.display ?? 'form', components: Array.isArray(p.components) ? p.components : [] });
    } catch {
      setBuilderForm({ display: 'form', components: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaTab, schemaReady]);

  const parsedSchema = useCallback((): unknown | null => {
    try { return JSON.parse(schemaJson); }
    catch { return null; }
  }, [schemaJson]);

  const handleJsonChange = (val: string) => {
    setSchemaJson(val);
    try { JSON.parse(val); setJsonError(null); }
    catch { setJsonError('Invalid JSON'); }
  };

  // The Form.io builder calls onChange(form) on every component add/edit/remove.
  // We capture the schema OUT to schemaJson (and keep `builderForm` ref stable so
  // the builder is not reset). JSON is stringified so the existing save path works.
  const handleDesignerChange = useCallback((form: unknown) => {
    setSchemaJson(JSON.stringify(form, null, 2));
    setJsonError(null);
  }, []);

  const handleGenerate = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt || aiBuilding) return;
    setAiBuilding(true);
    setAiBuildError(null);
    try {
      const res = await ai.buildForm(prompt);
      if (res.success && res.data?.schema) {
        setSchemaJson(JSON.stringify(res.data.schema, null, 2));
        setJsonError(null);
        setAiGenerated(true);
      } else {
        // Surface the server error verbatim (handles the current 503 stub
        // message so the panel works unchanged once the backend is enabled).
        setAiBuildError(res.error ?? 'Generation failed');
      }
    } catch {
      setAiBuildError('Could not reach the AI form builder. Please try again.');
    } finally {
      setAiBuilding(false);
    }
  };

  // Count of fields in the currently-generated draft (for the result card).
  const generatedFieldCount = (() => {
    try {
      const parsed = JSON.parse(schemaJson) as { components?: unknown[] };
      return Array.isArray(parsed.components) ? parsed.components.length : 0;
    } catch {
      return 0;
    }
  })();

  const handleSave = async () => {
    if (!name.trim()) { setSaveError('Form name is required'); return; }
    const schema = parsedSchema();
    if (!schema) { setSaveError('Schema JSON is invalid'); return; }
    if (!(schema as Record<string, unknown>)['components']) {
      setSaveError('Schema must have a "components" array'); return;
    }

    setSaving(true); setSaveError(null);
    try {
      if (editingSchema) {
        const res = await formsApi.schemas.update(editingSchema.id, {
          name: name.trim(), description, aiMode, authMode, isPublic,
          publicSlug: isPublic ? publicSlug : undefined,
          schemaJson: schema,
          // Always send: NULL = canonical DEFAULT, [] = staff opted out.
          identityFields: isDefaultConfig(identityFields) ? null : identityFields,
        });
        if (res.success && res.data) onSaved(res.data.id);
        else setSaveError(res.error ?? 'Save failed');
      } else {
        const res = await formsApi.schemas.create({
          name: name.trim(), description, aiMode, authMode, isPublic,
          publicSlug: isPublic && publicSlug ? publicSlug : undefined,
          schemaJson: schema,
          identityFields: isDefaultConfig(identityFields) ? null : identityFields,
        });
        if (res.success && res.data) onSaved(res.data.id);
        else setSaveError(res.error ?? 'Save failed');
      }
    } catch {
      setSaveError('Unexpected error saving form');
    } finally {
      setSaving(false);
    }
  };

  const preview = parsedSchema();
  const designerActive = schemaTab === 'designer';

  return (
    <div style={styles.root}>
      {/* Left panel — settings + schema editor */}
      <div style={{ ...styles.leftPanel, width: designerActive ? '100%' : '50%' }}>
        <div style={styles.leftHeader}>
          <h2 style={styles.heading}>{editingSchema ? 'Edit Form' : 'New Form'}</h2>
          <div style={styles.headerActions}>
            <button style={styles.cancelBtn} onClick={onCancel} disabled={saving}>Cancel</button>
            <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? (
                <span style={styles.btnInner}><SpinnerIcon size={14} />Saving…</span>
              ) : (
                <span style={styles.btnInner}><SaveIcon size={14} />Save Form</span>
              )}
            </button>
          </div>
        </div>
        {saveError && <div style={styles.errorBanner}>{saveError}</div>}

        <div style={styles.fields}>
          {/* Name */}
          <div style={styles.field}>
            <label style={styles.label}>Form Name *</label>
            <input style={styles.input} value={name} onChange={(e) => setName(e.target.value)} maxLength={255} />
          </div>

          {/* Description */}
          <div style={styles.field}>
            <label style={styles.label}>Description</label>
            <textarea style={{ ...styles.input, minHeight: 60 }} value={description} onChange={(e) => setDesc(e.target.value)} />
          </div>

          {/* AI Mode + Auth Mode */}
          <div style={styles.fieldRow}>
            <div style={styles.field}>
              <label style={styles.label}>AI Mode</label>
              <select style={styles.select} value={aiMode} onChange={(e) => setAiMode(e.target.value)}>
                {AI_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Authentication</label>
              <select style={styles.select} value={authMode} onChange={(e) => setAuthMode(e.target.value)}>
                {AUTH_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>

          {/* Public toggle */}
          <div style={styles.fieldRow}>
            <label style={styles.checkLabel}>
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
              <span>Make form publicly accessible</span>
            </label>
            {isPublic && (
              <div style={styles.field}>
                <label style={styles.label}>URL Slug (leave blank to auto-generate)</label>
                <input style={styles.input} value={publicSlug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} placeholder="e.g. patient-intake" />
              </div>
            )}
          </div>

          {/* Patient identity fields */}
          <div style={styles.field}>
            <label style={styles.label}>Patient identity fields</label>
            <p style={styles.identityCaption}>Patient identity collected before this form (packages override this)</p>
            <div style={styles.identityList}>
              {IDENTITY_FIELD_CATALOG.map((f) => {
                const included = isIncluded(identityFields, f.key);
                const required = identityFields.find((c) => c.key === f.key)?.required ?? false;
                return (
                  <div key={f.key} style={styles.identityRow}>
                    <label style={styles.identityInclude}>
                      <input
                        type="checkbox"
                        checked={included}
                        onChange={() => setIdentityFields((prev) => toggleField(prev, f.key))}
                      />
                      <span>{f.label}</span>
                    </label>
                    <label style={{ ...styles.identityRequired, opacity: included ? 1 : 0.4 }}>
                      <input
                        type="checkbox"
                        checked={required}
                        disabled={!included}
                        onChange={(e) => setIdentityFields((prev) => setRequired(prev, f.key, e.target.checked))}
                      />
                      <span>Required</span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Schema editor */}
          <div style={styles.field}>
            <label style={styles.label}>Form Schema</label>
            <div style={styles.schemaTabs}>
              <button
                style={{ ...styles.schemaTab, ...(schemaTab === 'designer' ? styles.schemaTabActive : {}) }}
                onClick={() => setSchemaTab('designer')}
              ><span style={styles.tabInner}><LayoutIcon size={14} />Designer</span></button>
              <button
                style={{ ...styles.schemaTab, ...(schemaTab === 'ai' ? styles.schemaTabActive : {}) }}
                onClick={() => setSchemaTab('ai')}
              ><span style={styles.tabInner}><SparkleIcon size={14} />AI Builder</span></button>
              <button
                style={{ ...styles.schemaTab, ...(schemaTab === 'json' ? styles.schemaTabActive : {}) }}
                onClick={() => setSchemaTab('json')}
              ><span style={styles.tabInner}><CodeIcon size={14} />JSON Editor</span></button>
            </div>

            {schemaTab === 'designer' ? (
              <div className="ci-formio-builder" style={styles.designerWrap}>
                {FormBuilderC && builderForm ? (
                  <FormBuilderC
                    form={builderForm}
                    options={{}}
                    onChange={handleDesignerChange}
                  />
                ) : (
                  <p style={styles.previewPlaceholder}>Loading the form designer…</p>
                )}
                <p style={styles.designerHint}>
                  Drag fields from the palette onto the canvas to build your form. Switch to <strong>JSON Editor</strong> for raw schema access, then back here to keep designing.
                </p>
              </div>
            ) : schemaTab === 'ai' ? (
              <div style={styles.aiBuilder}>
                <p style={styles.aiHelper}>
                  Describe the form in plain English. The AI drafts a Form.io schema you can review and edit.
                </p>
                <textarea
                  style={{ ...styles.input, minHeight: 96, resize: 'vertical' }}
                  value={aiPrompt}
                  onChange={(e) => { setAiPrompt(e.target.value); }}
                  placeholder="e.g. New-patient intake for a dental office: demographics, insurance, medical history checklist, and a signature."
                  disabled={aiBuilding}
                />
                <button
                  style={{ ...styles.saveBtn, alignSelf: 'flex-start', opacity: (!aiPrompt.trim() || aiBuilding) ? 0.6 : 1 }}
                  onClick={handleGenerate}
                  disabled={!aiPrompt.trim() || aiBuilding}
                  aria-busy={aiBuilding}
                >
                  {aiBuilding ? (
                    <span style={styles.btnInner}><SpinnerIcon size={14} />Generating…</span>
                  ) : (
                    <span style={styles.btnInner}><SparkleIcon size={14} />Generate schema</span>
                  )}
                </button>

                {aiBuildError && (
                  <div style={styles.errorBanner} role="alert">{aiBuildError}</div>
                )}

                {aiGenerated && !aiBuildError && (
                  <div style={styles.draftCard}>
                    <div style={styles.draftHeader}>
                      <span style={styles.draftIcon}><CheckCircleIcon size={16} /></span>
                      <strong style={styles.draftTitle}>
                        Draft schema · {generatedFieldCount} {generatedFieldCount === 1 ? 'field' : 'fields'}
                      </strong>
                    </div>
                    <p style={styles.draftSub}>
                      Review the generated schema in the Designer or JSON editor, then save.
                    </p>
                    <div style={styles.draftActions}>
                      <button style={styles.saveBtn} onClick={() => setSchemaTab('designer')}>
                        Open in Designer
                      </button>
                      <button
                        style={styles.cancelBtn}
                        onClick={() => { setAiGenerated(false); }}
                      >
                        Regenerate
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {jsonError && <div style={styles.errorBanner}>{jsonError}</div>}
                <textarea
                  style={{ ...styles.input, minHeight: 300, fontFamily: 'monospace', fontSize: 12 }}
                  value={schemaJson}
                  onChange={(e) => handleJsonChange(e.target.value)}
                  spellCheck={false}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right panel — live preview (hidden in Designer mode; the builder IS the preview) */}
      <div style={{ ...styles.rightPanel, display: designerActive ? 'none' : 'flex' }}>
        <div style={styles.previewHeader}>
          <span style={styles.previewTitle}>Live Preview</span>
        </div>
        <div style={styles.previewContent}>
          {preview && FormPreview ? (
            <FormPreview form={preview} options={{ readOnly: false }} />
          ) : (
            <p style={styles.previewPlaceholder}>
              {jsonError
                ? 'Fix the JSON errors to see a preview'
                : 'Define a form schema to see the preview'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root:        { display: 'flex', height: '100%', overflow: 'hidden' },
  leftPanel:   { width: '50%', display: 'flex', flexDirection: 'column', borderRight: '1px solid #e5e7eb', overflow: 'hidden' },
  leftHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 },
  heading:     { margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' },
  headerActions: { display: 'flex', gap: 8 },
  cancelBtn:   { background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: '#374151' },
  saveBtn:     { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  errorBanner: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '8px 16px', color: '#b91c1c', fontSize: 13, margin: '4px 20px' },
  fields:      { overflowY: 'auto', padding: '16px 20px', flex: 1 },
  field:       { marginBottom: 14 },
  fieldRow:    { display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' as const },
  label:       { display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 },
  input:       { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 13, color: '#111827', boxSizing: 'border-box' as const },
  select:      { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 13, color: '#111827' },
  checkLabel:  { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer', flex: 1 },
  identityCaption: { margin: '0 0 8px', fontSize: 11, color: '#9ca3af', lineHeight: 1.5 },
  identityList: { display: 'flex', flexDirection: 'column', gap: 4, border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 },
  identityRow:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '4px 6px' },
  identityInclude: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer' },
  identityRequired: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', cursor: 'pointer' },
  schemaTabs:  { display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 8 },
  schemaTab:   { padding: '6px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: '#6b7280' },
  schemaTabActive: { color: '#2563eb', borderBottom: '2px solid #2563eb' },
  designerWrap: { minHeight: 480, border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, background: '#fff' },
  designerHint: { margin: '10px 2px 0', fontSize: 12, color: '#6b7280', lineHeight: 1.5 },
  aiBuilder:   { display: 'flex', flexDirection: 'column', gap: 12 },
  aiHelper:    { margin: 0, fontSize: 12, color: '#6b7280', lineHeight: 1.5 },
  btnInner:    { display: 'inline-flex', alignItems: 'center', gap: 6 },
  tabInner:    { display: 'inline-flex', alignItems: 'center', gap: 6 },
  draftCard:   { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 },
  draftHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  draftIcon:   { display: 'flex', alignItems: 'center', color: '#2563eb' },
  draftTitle:  { fontSize: 13, color: '#111827' },
  draftSub:    { margin: '0 0 10px', fontSize: 12, color: '#6b7280', lineHeight: 1.5 },
  draftActions: { display: 'flex', gap: 8, flexWrap: 'wrap' as const },
  rightPanel:  { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  previewHeader: { padding: '14px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 },
  previewTitle:  { fontSize: 13, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  previewContent:  { flex: 1, overflowY: 'auto', padding: '16px 20px' },
  previewPlaceholder: { color: '#9ca3af', fontSize: 14 },
};
